import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import {
    Sizes,
    Colours,
    Typography,
    useExperiment,
    ExperimentSegments,
    ProductCardTabFooter,
} from '@ratehub/base-ui';

import EXPERIMENTS from '../definitions/Experiments';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';
import getCardFooterTabContent from '../functions/getCardFooterTabContent';


function CardLayoutFooter({
    cardInfo,
    borderRadius = '4px',
    isAWidget = false,
}) {
    const { segment } = useExperiment(EXPERIMENTS.openedDetails);
    const [ activeTab, setActiveTab ] = useState();
    const [ initialTabIndex, setInitialTabIndex ] = useState(null);

    // wait for experiment to resolve before setting initialTabIndex and activeTab
    useEffect(() => {
        if (segment !== null) {
            const newActiveTab = segment === ExperimentSegments.VARIANT_1
                ? FOOTER_TABS.details
                : activeTab;
            setActiveTab(newActiveTab);

            const foundTabIndex = Object.values(FOOTER_TABS).findIndex(value => value === newActiveTab);
            // initialTabIndex needs to be null if the activeTab is not found
            setInitialTabIndex(foundTabIndex !== -1
                ? foundTabIndex
                : null);
        }
    }, [ segment ]);

    const { ratehubReview, slug } = cardInfo;

    const missingReview = !ratehubReview?.content;     // ideally, this won’t happen, but…
    if (missingReview) {
        // a candidate for a future `noticeWarning()` function call
        // eslint-disable-next-line no-console
        console.warn(`[CardLayoutFooter] The credit card “${slug}” is missing its Ratehub review.`);
    }

    // assumes when operating as a widget we never want to show the Ratehub review (e.g. MS may have their own review)
    const cardTabBar = getTabBar({ includeRatehubReview: !missingReview && !isAWidget });
    const tabContentPanels = getCardFooterTabContent({
        cardInfo,
        includeRatehubReview: !missingReview && !isAWidget,
        footerTabNames: FOOTER_TABS,
    });

    return (
        <CreditCardLayoutFooter
            initialTabIndex={initialTabIndex}
            borderRadius={borderRadius}
            tabBar={cardTabBar}
            tabPanelContent={tabContentPanels}
            productId={cardInfo.id}
        />
    );
}

CardLayoutFooter.propTypes = {
    cardInfo: PropTypes.shape(CreditCardSynopsisShape).isRequired,
    borderRadius: PropTypes.string,
    isAWidget: PropTypes.bool,
};


function getTabBar({ includeRatehubReview }) {
    const tabBar = [ {
        label: <FormattedMessage {...LOCAL_MESSAGES.perks} />,
        value: FOOTER_TABS.perks,
    } ];

    if (includeRatehubReview) {
        tabBar.push({
            label: <FormattedMessage {...LOCAL_MESSAGES.prosAndCons} />,
            value: FOOTER_TABS.prosAndCons,
        });
    }

    tabBar.push({
        label: <FormattedMessage {...LOCAL_MESSAGES.details} />,
        value: FOOTER_TABS.details,
    });

    return tabBar;
}


const FOOTER_TABS = {
    // values must be id-worthy
    perks: 'perks',
    prosAndCons: 'pros-and-cons',
    details: 'details',
};

const LOCAL_MESSAGES = defineMessages({
    perks: {
        id: 'edb.credit-card.footer.perks',
        defaultMessage: 'Perks',
    },
    prosAndCons: {
        id: 'edb.credit-card.footer.pros-and-cons',
        defaultMessage: 'Pros & cons',
    },
    details: {
        id: 'edb.credit-card.footer.details',
        defaultMessage: 'Details',
    },
});


const CreditCardLayoutFooter = styled(ProductCardTabFooter)`
    // this is styling for the CardFeaturesTable in Perks
    .card-features-table-wrapper {
        max-width: 38rem;

        .card-features-table-details-column {
            min-width: 9rem;
        }
    }

    // this is styling for markup provided by the CMS
    .bonus-description-markup,
    .ratehub-review-markup {
        max-width: 57em;
        h3 {
            font-size: ${Sizes.FONTS.XS};
            font-weight: ${Typography.WEIGHTS.MEDIUM};
            margin-bottom: ${Sizes.SPACING.SEVEN_EIGHTHS};
        }
        // to apply to all ul > li’s, regardless of nesting
        ul > li {
            margin-bottom: ${Sizes.SPACING.QUARTER};
            &:last-of-type {
                margin-bottom: unset;
            }
            &::marker {
                color: ${Colours.BLUEBERRY_DARK};
                line-height: 1;
            }
        }
        ol > li {
            padding-left: ${Sizes.SPACING.QUARTER};
        }
        > ul {
            padding-left: ${Sizes.SPACING.ONE_AND_A_QUARTER};
            > li {
                list-style-type: disc;
                > ul {
                    padding-left: ${Sizes.SPACING.TWO};
                    margin-top: ${Sizes.SPACING.QUARTER};
                    margin-bottom: ${Sizes.SPACING.QUARTER};
                    > li {
                        list-style-type: circle;
                    }
                }
            }
        }
    }
`;


export default observer(CardLayoutFooter);
